import React, { useState, useEffect, useCallback, useContext } from 'react';
import { Table, Button, Tag, Empty, Typography, Checkbox, Space, Select, DatePicker, Segmented, Popconfirm, Input, message } from 'antd';
import { PlusOutlined, TrophyOutlined, EditOutlined, DeleteOutlined, CheckOutlined, CloseOutlined } from '@ant-design/icons';
import { Link } from 'react-router-dom';
import dayjs from 'dayjs';
import api from '../../api';
import { UserContext } from '../../App';
import AddEmployeeBadgeModal from '../AddEmployeeBadgeModal';

/**
 * Badges held by the selected team's active members, expired ones included,
 * ordered by employee. Add Badge (CanAssignBadge) is limited to the team.
 * Not tied to the date window: a badge is not an activity in a period.
 */
export default function BadgesTab({ teamId }) {
  const currentUser = useContext(UserContext);
  const canAssign      = (currentUser?.CanAssignBadge ?? 0) >= 1;
  const canViewSummary = (currentUser?.CanViewAgentSummary ?? 0) >= 1;

  const [members, setMembers] = useState([]);
  const [rows,    setRows]    = useState([]);
  const [loading, setLoading] = useState(false);
  const [addOpen, setAddOpen] = useState(false);
  // Everything is fetched once; this only hides expired badges.
  const [showExpired, setShowExpired] = useState(false);
  // Edit in place, as in Manage Badges: {id, badge_id, mode, year_month, expires_on}.
  const [badgeTypes, setBadgeTypes] = useState([]);
  const [edit,       setEdit]       = useState(null);
  const [editSaving, setEditSaving] = useState(false);

  const load = useCallback(() => {
    if (!teamId) return;
    setLoading(true);
    api.get(`/api/teams/${teamId}/badges`)
      .then((r) => { setMembers(r.data.members || []); setRows(r.data.badges || []); })
      .catch(() => message.error('Failed to load badges.'))
      .finally(() => setLoading(false));
  }, [teamId]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    if (!canAssign) return;
    api.get('/api/badges', { params: { active_only: 1 } })
      .then((r) => setBadgeTypes(r.data || []))
      .catch(() => {});
  }, [canAssign]);

  const startEdit = (r) => {
    const ym = (r.YearMonth || '').trim();
    const isYear = ym.length === 4;
    setEdit({
      id:         r.ID,
      badge_id:   r.BadgeID,
      mode:       isYear ? 'year' : 'month',
      year_month: ym ? dayjs(isYear ? `${ym}-01-01` : `${ym}-01`) : null,
      expires_on: r.ExpiresOn ? dayjs(r.ExpiresOn) : null,
      special_note: r.SpecialNote || '',
    });
  };

  const saveEdit = async () => {
    if (!edit?.badge_id) { message.warning('Pick a badge.'); return; }
    setEditSaving(true);
    try {
      await api.put(`/api/user-badges/${edit.id}`, {
        badge_id:   edit.badge_id,
        year_month: edit.year_month ? edit.year_month.format(edit.mode === 'year' ? 'YYYY' : 'YYYY-MM') : null,
        expires_on: edit.expires_on ? edit.expires_on.format('YYYY-MM-DD') : null,
        special_note: edit.special_note || null,
      });
      message.success('Badge updated.');
      setEdit(null);
      load();
    } catch (err) {
      message.error(err.response?.data?.error || 'Save failed.');
    } finally {
      setEditSaving(false);
    }
  };

  const remove = async (id) => {
    try {
      await api.delete(`/api/user-badges/${id}`);
      message.success('Badge removed.');
      if (edit?.id === id) setEdit(null);
      load();
    } catch (err) {
      message.error(err.response?.data?.error || 'Delete failed.');
    }
  };

  if (!teamId) {
    return <Empty description="Select a team" image={Empty.PRESENTED_IMAGE_SIMPLE} style={{ marginTop: 40 }} />;
  }

  const today = dayjs().format('YYYY-MM-DD');
  const isExpired = (r) => !!r.ExpiresOn && String(r.ExpiresOn).slice(0, 10) < today;
  const shown = showExpired ? rows : rows.filter((r) => !isExpired(r));
  const isEditing = (r) => edit?.id === r.ID;
  const setE = (patch) => setEdit((prev) => ({ ...prev, ...patch }));
  // The row's own badge stays selectable even if it has since been switched off.
  const optionsFor = (r) => {
    const opts = badgeTypes.map((b) => ({ value: b.ID, label: b.Name }));
    return badgeTypes.some((b) => b.ID === r.BadgeID)
      ? opts : [...opts, { value: r.BadgeID, label: `${r.BadgeName} (inactive)` }];
  };

  const columns = [
    { title: 'Employee', dataIndex: 'EmployeeName', key: 'EmployeeName', width: 220,
      sorter: (a, b) => (a.EmployeeName || '').localeCompare(b.EmployeeName || ''),
      render: (v, r) => (canViewSummary ? <Link to={`/agents?user_id=${r.UserID}`}>{v}</Link> : v) },
    { title: 'Badge', dataIndex: 'BadgeName', key: 'BadgeName',
      sorter: (a, b) => (a.BadgeName || '').localeCompare(b.BadgeName || ''),
      render: (v, r) => (isEditing(r)
        ? <Select size="small" value={edit.badge_id} options={optionsFor(r)} showSearch optionFilterProp="label"
                  style={{ minWidth: 200 }} onChange={(id) => setE({ badge_id: id })} />
        : (
          <>
            <Tag color={r.BadgeIsActive ? 'gold' : 'default'} icon={<TrophyOutlined />}>{v}</Tag>
            {!r.BadgeIsActive && <Tag>inactive</Tag>}
          </>
        )) },
    { title: 'Year-Month', dataIndex: 'YearMonth', key: 'YearMonth', width: 110,
      sorter: (a, b) => (a.YearMonth || '').localeCompare(b.YearMonth || ''),
      render: (v, r) => (isEditing(r)
        ? (
          <Space size={4}>
            <Segmented size="small" value={edit.mode} onChange={(m) => setE({ mode: m, year_month: null })}
                       options={[{ label: 'Month', value: 'month' }, { label: 'Year', value: 'year' }]} />
            <DatePicker size="small" picker={edit.mode} value={edit.year_month}
                        format={edit.mode === 'year' ? 'YYYY' : 'YYYY-MM'} style={{ width: 95 }}
                        onChange={(d) => setE({ year_month: d })} />
          </Space>
        )
        : (v || '—')) },
    { title: 'Expires On', dataIndex: 'ExpiresOn', key: 'ExpiresOn', width: 150,
      sorter: (a, b) => String(a.ExpiresOn || '').localeCompare(String(b.ExpiresOn || '')),
      render: (v, r) => (isEditing(r)
        ? <DatePicker size="small" value={edit.expires_on} format="YYYY-MM-DD" style={{ width: 130 }}
                      onChange={(d) => setE({ expires_on: d })} />
        : !v ? '—'
          : <>{String(v).slice(0, 10)}{String(v).slice(0, 10) < today && <Tag color="red" style={{ marginLeft: 6 }}>expired</Tag>}</>) },
    { title: 'Special Note', dataIndex: 'SpecialNote', key: 'SpecialNote', width: 200,
      render: (v, r) => (isEditing(r)
        ? <Input size="small" value={edit.special_note} maxLength={100} onChange={(e) => setE({ special_note: e.target.value })} />
        : (v || '—')) },
    { title: 'Assigned By', dataIndex: 'AssignedByName', key: 'AssignedByName', width: 160,
      render: (v) => v || '—' },
    ...(canAssign ? [{
      title: '', key: 'actions', width: 90, align: 'right',
      render: (_, r) => (isEditing(r)
        ? (
          <Space size={4}>
            <Button size="small" type="primary" icon={<CheckOutlined />} loading={editSaving} onClick={saveEdit} />
            <Button size="small" icon={<CloseOutlined />} onClick={() => setEdit(null)} />
          </Space>
        )
        : (
          <Space size={4}>
            <Button size="small" icon={<EditOutlined />} disabled={!!edit} onClick={() => startEdit(r)} />
            <Popconfirm title="Remove this badge?" okText="Remove" okButtonProps={{ danger: true }} onConfirm={() => remove(r.ID)}>
              <Button size="small" danger icon={<DeleteOutlined />} disabled={!!edit} />
            </Popconfirm>
          </Space>
        )),
    }] : []),
  ];

  return (
    <>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
        <Space size={16}>
          <Typography.Text type="secondary">
            {shown.length} badge{shown.length === 1 ? '' : 's'} across {new Set(shown.map((r) => r.UserID)).size} of {members.length} team members
          </Typography.Text>
          <Checkbox checked={showExpired} onChange={(e) => setShowExpired(e.target.checked)}>
            Include expired badges
          </Checkbox>
        </Space>
        {canAssign && (
          <Button type="primary" icon={<PlusOutlined />} onClick={() => setAddOpen(true)} disabled={!members.length}>
            Add Badge
          </Button>
        )}
      </div>

      <Table
        rowKey="ID"
        dataSource={shown}
        columns={columns}
        loading={loading}
        size="small"
        pagination={{ defaultPageSize: 50, showSizeChanger: true }}
        scroll={{ x: 'max-content' }}
        locale={{ emptyText: 'No badges on this team yet.' }}
        rowClassName={(r) => (isEditing(r) ? 'ant-table-row-selected' : '')}
      />

      <AddEmployeeBadgeModal
        open={addOpen}
        employees={members}
        onClose={() => setAddOpen(false)}
        onAdded={load}
      />
    </>
  );
}
